import React, { useEffect, useState, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { itemsPerPage, maxPageNumberForFullLoad } from '../../variables';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@welldev-group/rcl-button';
import Header from '../common/Header';
import Paginate from '../common/Paginate';
import EmptyContentPlaceholder from '../common/EmptyContentPlaceholder';
import Input from '@welldev-group/rcl-input';
import GET_COUNT_OF_TOTAL_CURSORS from '../../graphql/queries/totalCursorsCount';
import { GET_FAQS, GET_CURSORS_FOR_FAQS } from '../../graphql/queries/faq';
import { debounce } from 'lodash';
import FaqData from './FaqData';
import PropTypes from 'prop-types';
import SpinnerIcon from '../../assets/icons/spinner.svg';
import IconButton from '../rclComponents/IconButton';
import Icon from '../icon/Icon';
import Hashtag from '../hashtag';
import isEntityAvailable from '../../helpers/isEntityAvailable';

const FaqsList = props => {
  const { t } = useTranslation();

  const [cursorState, setCursorState] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(3);
  const [currentCursor, setCurrentCursor] = useState(null);
  const [searchParam, setSearchParam] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [cursors, setCursors] = useState([]);
  const [cursorFetchNumber, setCursorFetchNumber] = useState(itemsPerPage * 4);
  const [requiredLenghToSearch, setRequiredLengthToSearch] = useState(3);

  const cursorData = useQuery(GET_CURSORS_FOR_FAQS, {
    variables: { first: cursorFetchNumber, after: currentCursor, query: searchParam },
  });
  const cursorCountData = useQuery(GET_COUNT_OF_TOTAL_CURSORS, {
    variables: { modelName: 'faqs', query: searchParam, currentTab: 0 },
  });
  const { loading, data } = useQuery(GET_FAQS, {
    variables: { first: itemsPerPage, after: currentCursor, query: searchParam },
  });

  const callSearchParam = useRef(
    debounce(async inputParam => {
      setSearchParam(inputParam);
    }, 1000),
  );

  const resetSearch = () => {
    setSearchParam('');
    setSearchValue('');
  };

  const postIcon = () => {
    if (loading) {
      return <img className="list__search-post-icon" src={SpinnerIcon} alt="spinner" />;
    }
    return (
      <IconButton
        size="large"
        className="list__search-post-icon"
        isIconOnly={true}
        icon={<Icon name="close" />}
        onClick={resetSearch}
      />
    );
  };

  const forcePage = currentPage - 1;

  useEffect(() => {
    if (cursorCountData && cursorCountData.data && !cursorCountData.loading) {
      const count = cursorCountData.data.totalCursorsCount / itemsPerPage;
      if (count <= maxPageNumberForFullLoad) {
        setRequiredLengthToSearch(2);
        setCursorFetchNumber(null);
      } else {
        setRequiredLengthToSearch(3);
        setCursorFetchNumber(itemsPerPage * 4);
      }
      setPageCount(Math.ceil(count));
    }
    if (cursorData && cursorData.data && !cursorData.loading && !cursorState) {
      setCursorState(true);
      const oldCursors = Object.assign([], cursors);
      const newCursors = cursorData.data['faqs'].edges.map(edge => edge.cursor);
      const finalCursors = oldCursors.concat(
        newCursors.filter(newCursor => oldCursors.indexOf(newCursor) < 0),
      );
      setCursors(finalCursors);
    }
  }, [cursorCountData, cursorData, cursors, cursorState]);

  const changePage = value => {
    setCurrentPage(value.selected + 1);
    const cursorIndexFromCurrentPage = value.selected * itemsPerPage - 1;
    if (cursorIndexFromCurrentPage < 0) {
      setCurrentCursor(null);
    } else {
      setCurrentCursor(cursors[cursorIndexFromCurrentPage]);
    }
  };

  const onSearchParamChange = value => {
    setSearchValue(value);
    if (value === '' || value.length >= requiredLenghToSearch) {
      callSearchParam.current(value);
    }
  };

  return (
    <div className="faqs-list">
      <Header title="nav.faqs" linkTo="/admin/faqs/new" buttonLabel="faqs.buttons.addNew">
        <div className="header__children">
          <Link to={`/admin/faq_labels/new`}>
            <Button label={t('faqs.buttons.addNewLabel')} />
          </Link>
          <div className="hashtag-wrapper">
            <Hashtag
              headerLabel="faqs.attributes.keywords"
              currentContentLabel="faqs.index.currentKeywords"
            />
          </div>
        </div>
      </Header>
      <div>
        <Input
          label={t('list.search.label')}
          value={searchValue}
          onChange={onSearchParamChange}
          placeholder={t('faqs.index.searchPlaceholder')}
          postIcon={postIcon()}
        />
      </div>
      {loading && <div className="lds-dual-ring" />}
      {!loading && (
        <React.Fragment>
          {isEntityAvailable(data, 'faqs') ? (
            <>
              <div className="faqs-list__pagination">
                <Paginate
                  pageCount={pageCount}
                  onPageChange={changePage}
                  forcePage={forcePage}
                  marginPagesDisplayed={cursorFetchNumber ? 0 : 1}
                />
              </div>
              <div className="faqs-list__header">
                <div className="faqs-list__value-topic faqs-list__value-title" key={'label_topic'}>
                  <p>{t('faqs.attributes.labelTopic')}</p>
                </div>
                {['order', 'rating'].map(item => (
                  <div className="faqs-list__value faqs-list__value-title" key={item}>
                    <p>{t(`faqs.attributes.${item}`)}</p>
                  </div>
                ))}
                <div className="faqs-list__value faqs-list__value--actions">
                  <p>{t('list.header.actions')}</p>
                </div>
              </div>

              {data['faqs'] && (
                <div className="faqs-list__table">
                  {data['faqs'].edges.map(data => (
                    <React.Fragment key={data.node.id}>
                      <FaqData
                        data={data.node}
                        searchParam={searchParam}
                        currentCursor={currentCursor}
                        userId={props.userId}
                        depth={1}
                      />
                    </React.Fragment>
                  ))}
                </div>
              )}
              <div className="faqs-list__pagination">
                <Paginate
                  pageCount={pageCount}
                  onPageChange={changePage}
                  forcePage={forcePage}
                  marginPagesDisplayed={cursorFetchNumber ? 0 : 1}
                />
              </div>
            </>
          ) : (
            <EmptyContentPlaceholder entityName={t('nav.faqs').toLowerCase()} modelName={'Faq'} />
          )}
        </React.Fragment>
      )}
    </div>
  );
};

FaqsList.propTypes = {
  userId: PropTypes.string.isRequired,
};
export default FaqsList;
